import React, { useEffect, useRef } from "react";

const FONT = "'Space Grotesk', sans-serif";

/**
 * ConfirmDialog — lightweight destructive-action confirmation modal.
 *
 * Props:
 *   title       string   — heading text
 *   message     string   — body copy
 *   confirmLabel string  — confirm button label (default "Delete")
 *   variant     "danger" | "primary"  — confirm button color (default "danger")
 *   onConfirm   () => void
 *   onCancel    () => void
 */
const ConfirmDialog = ({
  title = "Are you sure?",
  message,
  confirmLabel = "Delete",
  variant = "danger",
  onConfirm,
  onCancel,
}) => {
  const cancelRef = useRef(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const confirmCls =
    variant === "danger"
      ? "bg-red-500 hover:bg-red-600 text-white"
      : "bg-[#06b6d4] hover:bg-cyan-700 text-white";

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      style={{ fontFamily: FONT }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onCancel}
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Top accent */}
        <div className="h-1 w-full bg-gradient-to-r from-red-400 to-red-500" />

        <div className="px-6 pt-5 pb-6">
          {/* Icon + title */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <svg width="17" height="17" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-[#253439] leading-snug">{title}</h2>
          </div>

          {message && (
            <p className="text-sm text-gray-500 leading-relaxed mb-5 ml-12">{message}</p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              ref={cancelRef}
              onClick={onCancel}
              className="px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${confirmCls}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
